
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Resource } from "../hooks/useResourceManagement";

interface EditResourceAssignmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  assignment: any;
  formData: any;
  setFormData: (data: any) => void;
  onSave: () => void;
  resources?: Resource[];
}

const EditResourceAssignmentDialog = ({
  isOpen,
  onClose,
  assignment,
  formData,
  setFormData,
  onSave,
  resources = []
}: EditResourceAssignmentDialogProps) => {
  // Get the resource details for the assignment
  const assignedResource = resources.find(r => r.id === assignment?.resource_id);

  const handleAllocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: any) => ({ ...prev, allocated_percentage: e.target.value }));
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev: any) => ({ ...prev, duration: value }));
  };

  const handleDurationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    const numValue = Number(value);
    if (numValue < 1) {
      setFormData((prev: any) => ({ ...prev, duration: 1 }));
    } else if (numValue > 9999) {
      setFormData((prev: any) => ({ ...prev, duration: 9999 }));
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev: any) => ({ ...prev, allocation_hrs: value }));
  };

  const handleHoursBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    const numValue = Number(value);
    if (numValue < 1) {
      setFormData((prev: any) => ({ ...prev, allocation_hrs: '1' }));
    } else if (numValue > 24) {
      setFormData((prev: any) => ({ ...prev, allocation_hrs: '24' }));
    }
  };

  const formatRateWithCommas = (value: string) => {
    // Don't format if the user is still typing a decimal (trailing dot or decimal digits)
    if (value.includes('.')) return value;
    const num = parseFloat(value.replace(/,/g, ''));
    if (isNaN(num)) return value;
    return num >= 1000 ? num.toLocaleString('en-US') : value;
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Sanitize: remove commas and any characters except digits and a single decimal point
    const input = e.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');

    // Allow at most one decimal point
    const parts = input.split('.');
    const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : input;

    // Preserve raw string during typing (don't convert to Number, which strips trailing dots/decimals)
    setFormData((prev: any) => ({ ...prev, rate: sanitized }));
  };

  const handleAllocationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    
    const numValue = Number(value);
    if (numValue < 1) {
      setFormData((prev: any) => ({ ...prev, allocated_percentage: 1 }));
    } else if (numValue > 100) {
      setFormData((prev: any) => ({ ...prev, allocated_percentage: 100 }));
    }
  };

  const handleRateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') return;
    
    const raw = value.replace(/,/g, '');
    const numValue = Number(raw);
    if (isNaN(numValue)) return;
    if (numValue < 0) {
      setFormData((prev: any) => ({ ...prev, rate: '0' }));
    } else if (numValue > 100000) {
      setFormData((prev: any) => ({ ...prev, rate: '100000' }));
    } else {
      setFormData((prev: any) => ({ ...prev, rate: raw }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-white border-primary/20 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-black">Edit Resource Assignment</DialogTitle>
          <DialogDescription className="text-black">
            Update the assignment details for {assignment?.name || 'this resource'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <Label className="text-black font-semibold">Resource:</Label>
            <p className="text-black mt-1">{assignment?.name || 'Unknown Resource'} - {assignment?.role || 'Unknown Role'}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="role" className="text-black">Role</Label>
              <Input
                id="role"
                type="text"
                value={formData.role || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, role: e.target.value }))}
                maxLength={100}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="role_type" className="text-black">Role Type</Label>
              <Input
                id="role_type"
                type="text"
                value={formData.role_type || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, role_type: e.target.value }))}
                maxLength={100}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="duration" className="text-black">Duration (days, 1-9999)</Label>
              <Input
                id="duration"
                type="text"
                value={formData.duration ?? ''}
                onChange={handleDurationChange}
                onBlur={handleDurationBlur}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="hours" className="text-black">Hours (per day, 1-24)</Label>
              <Input
                id="hours"
                type="text"
                value={formData.allocation_hrs ?? ''}
                onChange={handleHoursChange}
                onBlur={handleHoursBlur}
                className="bg-white border-gray text-black"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date" className="text-black">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, start_date: e.target.value }))}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="end_date" className="text-black">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, end_date: e.target.value }))}
                className="bg-white border-gray text-black"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="allocated_percentage" className="text-black">Allocation % (1-100)</Label>
              <Input
                id="allocated_percentage"
                type="number"
                min={1}
                max={100}
                value={formData.allocated_percentage}
                onChange={handleAllocationChange}
                onBlur={handleAllocationBlur}
                className="bg-white border-gray text-black"
              />
            </div>
            <div>
              <Label htmlFor="rate" className="text-black">Rate ($/hour) ($0-$100,000)</Label>
              <Input
                id="rate"
                type="text"
                value={formatRateWithCommas(String(formData.rate ?? ''))}
                onChange={handleRateChange}
                onBlur={handleRateBlur}
                className="bg-white border-gray text-black"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-1 border-primary/20 bg-white text-black">
            Cancel
          </Button>
          <Button 
            onClick={onSave} 
            className="bg-primary hover:bg-primary/90 text-black"
          >
            Update Resource
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditResourceAssignmentDialog;
